// ============================================================================
// SECOND SCREEN LAYOUT EDITOR - Full-screen modal for customizing widget layout
// Drag-and-drop grid editor with widget palette
// ============================================================================

import React, { useState, useMemo, useCallback } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { GridLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import { WIDGET_REGISTRY } from '../../second-screen/widgets/widgetRegistry';
import type { SecondScreenLayout, WidgetInstance, WidgetDefinition } from '../../../types/second-screen.types';

const LAYOUT_KEY_PREFIX = 'second-screen-layout-';

interface SecondScreenLayoutEditorProps {
  sessionId: string;
  screenNumber: number;
  initialLayout: SecondScreenLayout;
  onClose: () => void;
}

let nextInstanceId = 100;
function generateInstanceId(widgetId: string): string {
  return `custom-${widgetId}-${++nextInstanceId}`;
}

const SecondScreenLayoutEditor: React.FC<SecondScreenLayoutEditorProps> = ({
  sessionId,
  screenNumber,
  initialLayout,
  onClose,
}) => {
  const [widgets, setWidgets] = useState<WidgetInstance[]>(() => [...initialLayout.widgets]);
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);

  const cols = initialLayout.cols || 12;
  const editorWidth = Math.min(1200, window.innerWidth - 320);
  const rowHeight = 60;

  const gridLayout = useMemo(() => {
    return widgets.map(w => ({
      i: w.instanceId,
      x: w.x,
      y: w.y,
      w: w.w,
      h: w.h,
      minW: WIDGET_REGISTRY[w.widgetId]?.minW ?? 2,
      minH: WIDGET_REGISTRY[w.widgetId]?.minH ?? 1,
    }));
  }, [widgets]);

  const handleLayoutChange = useCallback((newLayout: Array<{ i: string; x: number; y: number; w: number; h: number }>) => {
    setWidgets(prev => prev.map(widget => {
      const layoutItem = newLayout.find(l => l.i === widget.instanceId);
      if (layoutItem) {
        return { ...widget, x: layoutItem.x, y: layoutItem.y, w: layoutItem.w, h: layoutItem.h };
      }
      return widget;
    }));
  }, []);

  const addWidget = (definition: WidgetDefinition) => {
    const newWidget: WidgetInstance = {
      instanceId: generateInstanceId(definition.id),
      widgetId: definition.id,
      config: {},
      x: 0,
      y: Infinity, // react-grid-layout will place it at the bottom
      w: definition.defaultW,
      h: definition.defaultH,
    };
    setWidgets(prev => [...prev, newWidget]);
  };

  const removeWidget = (instanceId: string) => {
    setWidgets(prev => prev.filter(w => w.instanceId !== instanceId));
    if (selectedWidgetId === instanceId) setSelectedWidgetId(null);
  };

  const handleSave = () => {
    const updatedLayout: SecondScreenLayout = {
      ...initialLayout,
      id: 'custom',
      name: 'Custom',
      widgets,
    };
    localStorage.setItem(`${LAYOUT_KEY_PREFIX}${sessionId}-screen${screenNumber}`, JSON.stringify(updatedLayout));
    onClose();
  };

  // Group available widgets by category
  const widgetsByCategory = useMemo(() => {
    const cats = new Map<string, WidgetDefinition[]>();
    for (const def of Object.values(WIDGET_REGISTRY)) {
      const list = cats.get(def.category) || [];
      list.push(def);
      cats.set(def.category, list);
    }
    return cats;
  }, []);

  const categoryLabels: Record<string, string> = {
    avatars: 'Avatars',
    session: 'Session',
    content: 'Content',
    data: 'Data & Intelligence',
    engagement: 'Engagement',
  };

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200 bg-white shrink-0">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Layout Editor</h2>
          <p className="text-xs text-slate-500">Drag and resize widgets to customize your second screen layout</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors"
          >
            Save Layout
          </button>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Widget palette (left) */}
        <div className="w-72 border-r border-slate-200 overflow-y-auto p-4 space-y-4 shrink-0">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Add Widgets</h3>

          {Array.from(widgetsByCategory.entries()).map(([category, defs]) => (
            <div key={category}>
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">
                {categoryLabels[category] || category}
              </h4>
              <div className="space-y-1">
                {defs.map(def => {
                  const Icon = def.icon;
                  return (
                    <button
                      key={def.id}
                      onClick={() => addWidget(def)}
                      className="w-full flex items-center gap-2.5 p-2 rounded-lg text-left hover:bg-primary/5 hover:border-primary/20 border border-transparent transition-colors"
                    >
                      <Icon size={14} className="text-slate-400 shrink-0" />
                      <div className="min-w-0">
                        <span className="text-xs font-semibold text-slate-700 block">{def.label}</span>
                        <span className="text-[10px] text-slate-400 block truncate">{def.description}</span>
                      </div>
                      <Plus size={12} className="text-slate-300 shrink-0 ml-auto" />
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Grid editor (center) */}
        <div className="flex-1 overflow-auto bg-slate-50 p-4">
          <div
            className="mx-auto bg-navy/5 rounded-xl border-2 border-dashed border-navy/20 min-h-[480px]"
            style={{ width: editorWidth }}
          >
            <GridLayout
              className="layout"
              layout={gridLayout}
              cols={cols}
              rowHeight={rowHeight}
              width={editorWidth}
              margin={[8, 8]}
              containerPadding={[8, 8]}
              isDraggable={true}
              isResizable={true}
              compactType="vertical"
              onLayoutChange={handleLayoutChange}
            >
              {widgets.map(widget => {
                const def = WIDGET_REGISTRY[widget.widgetId];
                const Icon = def?.icon;
                return (
                  <div
                    key={widget.instanceId}
                    className={`rounded-xl border-2 cursor-move flex items-center justify-center relative group ${
                      selectedWidgetId === widget.instanceId
                        ? 'bg-primary/10 border-primary/40'
                        : 'bg-white border-slate-200 hover:border-primary/30'
                    }`}
                    onClick={() => setSelectedWidgetId(widget.instanceId)}
                  >
                    <div className="flex flex-col items-center gap-1 text-center px-2">
                      {Icon && <Icon size={16} className="text-slate-400" />}
                      <span className="text-xs font-medium text-slate-600">{def?.label || widget.widgetId}</span>
                    </div>

                    {/* Remove button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeWidget(widget.instanceId);
                      }}
                      className="absolute top-1 right-1 p-1 rounded-full bg-red-500/10 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500/20"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                );
              })}
            </GridLayout>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecondScreenLayoutEditor;
